import React from "react";
import SEO from "../components/SEO";
import Footer from "../components/Footer";
import ShiftITLogo from "../images/ShiftITLogo.png";
import {
  Page,
  HeaderBar,
  HeaderInner,
  LogoLink,
  LogoImg,
  Main,
  Article,
  ArticleHeader,
  Title,
  Content,
} from "../components/legal/LegalElements";

export default function TermsPage() {
  return (
    <Page>
      <SEO
        title="Terms & Conditions | Shift IT"
        description="Review the Shift IT Terms & Conditions governing use of our website, services, assessments, and related materials."
        canonical="https://shift-it.io/terms"
      />
      <HeaderBar>
        <HeaderInner>
          <LogoLink href="/" aria-label="Go to home">
            <LogoImg src={ShiftITLogo} alt="Shift IT Logo" />
          </LogoLink>
        </HeaderInner>
      </HeaderBar>
      <Main>
        <Article>
          <ArticleHeader>
            <Title>Terms &amp; Conditions</Title>
          </ArticleHeader>
          <Content>
            <p>
              <strong>Updated: August 26, 2025</strong>
            </p>
            <h2>Purpose of this document</h2>
            <p>
              These Terms &amp; Conditions (the "Conditions") set out the basis
              on which Shift IT LLC ("We", "Us", "Our") supplies goods and
              services to clients ("You", "Your"). They are formatted for
              clarity and consistency. A quick‑read summary appears below; the
              legally binding terms follow.
            </p>
            <h2>Quick‑read summary (non‑binding)</h2>
            <ul>
              <li>
                Quotes are valid for 7 days and are not binding until we accept
                your order and receive payment where required.
              </li>
              <li>
                Pricing can change (e.g., if suppliers change prices). Taxes and
                out‑of‑pocket expenses are extra.
              </li>
              <li>
                Title to goods stays with us until paid in full. Risk passes on
                delivery.
              </li>
              <li>
                Returns follow manufacturer policies; customized/special items
                generally can't be returned.
              </li>
              <li>
                We limit liability to the extent permitted by law. You're
                responsible for your backups.
              </li>
              <li>
                We may subcontract work but remain responsible for delivery.
              </li>
              <li>
                Service requests must be logged via your Shift IT support portal
                (see Appendix A). After‑hours requests by phone (After Hours
                rates apply).
              </li>
              <li>
                Minimum commitment runs from the 1st of the month after you
                approve our quote. Auto‑renews for the same term unless you
                cancel per section 4.
              </li>
              <li>
                Either party can terminate with notice as set out in section 4.
              </li>
            </ul>
            <p>
              (The summary is for convenience only; the terms below control.)
            </p>
            <h2>1. Definitions and Interpretation</h2>
            <p>
              In these Conditions, the rate schedule and every quote, order,
              plan, contract, or other arrangement in connection with the supply
              of goods or services by Shift IT LLC the following words have the
              following meanings:
            </p>
            <p>
              "After Hours" means 5:00 PM to 8:00 AM Monday--Friday and all-day
              Saturday, Sunday, and Public Holidays.
            </p>
            <p>
              "Business Hours" means 8:00 AM to 5:00 PM Monday--Friday,
              excluding Public Holidays.
            </p>
            <p>
              "Client", "You", "Your" means any person or entity that seeks or
              obtains a quote for, or orders, Goods or Services from us,
              including the person named on an order or email placing an order
              and anyone on whose behalf an order is placed, and their permitted
              successors and assigns.
            </p>
            <p>"Conditions" means these Terms &amp; Conditions.</p>
            <p>
              "Goods" means any goods supplied by us (including computer
              hardware and Software) and any services we provide in connection
              with such goods.
            </p>
            <p>
              "Order" means any order you submit to us for Goods or Services in
              any form.
            </p>
            <p>
              "Period" means any agreed period (e.g., half‑days, days, weeks,
              months) during which Services will be provided.
            </p>
            <p>
              "Plan" means any arrangement between us and you (alone or with
              others) for Services (including unlimited support) and/or the
              provision of Goods under an arrangement for work to be done for
              you, including as set out in any Plan Schedule.
            </p>
            <p>
              "Plan Schedule" means the key terms applicable to a Plan as set
              (and varied) by us from time to time in our discretion.
            </p>
            <p>
              "Public Holidays" means any U.S. federal public holiday and any
              public holiday in the State of Georgia.
            </p>
            <p>
              "Rates" means our hourly rates and other charges for Services
              (including call‑out and return/cancellation fees) set out in a
              Rate Schedule, Plan, Plan Schedule, quote, contract, arrangement,
              or these Conditions, and includes any amount payable on a quantum
              meruit basis for work performed.
            </p>
            <p>
              "Rate Schedule" means Shift IT's schedule of rates, charges, and
              commercial conditions as set (and varied) by us from time to time.
            </p>
            <p>
              "Reasonable Assistance Limits" has the meaning in section 17.2.
            </p>
            <p>
              "Return/Cancellation Fee" means a fee charged under section 12.5.
            </p>
            <p>
              "Service Request" means a request for service such as adds, moves,
              changes, incidents, or technical assistance.
            </p>
            <p>
              "Services" means any services we provide, including Work, advice,
              and recommendations.
            </p>
            <p>
              "Software" includes software and any installation, updates,
              associated software, and related services.
            </p>
            <p>
              "We", "us", "our" means Shift IT LLC and its successors and
              assigns.
            </p>
            <p>
              "Work" means anything we may do, provide, customize, produce, or
              acquire for your benefit, including testing, troubleshooting,
              installation and configuration of equipment or software,
              consulting, scoping, planning, documenting, and quoting.
            </p>
            <p>
              Interpretation. Unless the contrary intention appears: (a)
              singular includes plural and vice versa; (b) a reference to a
              gender includes all genders; (c) a reference to any law includes
              amendments and re‑enactments; (d) headings are for convenience
              only; (e) "\$" means U.S. dollars; (f) time is Eastern Time
              (U.S.); (g) "person" includes any entity; (h) references to
              recitals/clauses/schedules/annexures form part of these
              Conditions; (i) references to agreements include amendments,
              novations, and replacements; (j) defined terms include grammatical
              variants; (k) "includes" means "including, without limitation";
              (l) "will" imports a condition, not a warranty; and (m) references
              to bankruptcy/winding up include analogous events under applicable
              law.
            </p>
            <h2>2. Application of these Conditions</h2>
            <p>
              Unless otherwise agreed in writing, these Conditions are
              incorporated into and prevail over any inconsistent term of every
              quote, Order, Plan, contract, or arrangement for the supply of
              Goods and/or Services by us to you. If any provision is invalid or
              unenforceable, the remaining provisions remain in full force and
              effect.
            </p>
            <h2>3. Commitment Term</h2>
            <p>
              3.1 The minimum term is as outlined in our quote to you and begins
              on the first day of the month following the date you sign or
              approve the quote.
            </p>
            <p>
              3.2 After the committed term ends, the term automatically renews
              for successive periods equal to the original committed term unless
              terminated under section 4.
            </p>
            <h2>4. Termination</h2>
            <p>
              4.1 You may terminate on ninety (90) days' written notice if we:
              (a) materially fail to fulfil our obligations and do not cure
              within thirty (30) days of notice; (b) breach a material term and
              do not remedy within thirty (30) days; or (c) terminate or suspend
              business operations (unless succeeded by a permitted assignee).
            </p>
            <p>4.2 We may terminate on ninety (90) days' written notice.</p>
            <p>
              4.3 On termination, we will provide reasonable transition
              assistance (including transfer to another provider) at our
              then‑current Rates (see Rate Schedule).
            </p>
            <p>
              4.4 If you terminate before the end of a committed term, you must
              pay all remaining amounts due through the end of that term.
            </p>
            <h2>5. Representations</h2>
            <p>
              5.1 No employee or agent of Shift IT may make any representation,
              warranty, or promise about Goods or Services except as set out in
              these Conditions or in a signed writing.
            </p>
            <h2>6. Notices</h2>
            <p>
              6.1 Notices must be in writing and sent by email to the last
              notified email address for the recipient's designated contact.
              Notices are deemed received when sent unless a delivery failure is
              received.
            </p>
            <h2>7. Governing Law</h2>
            <p>
              7.1 These Conditions are governed by the laws of the State of
              Georgia. The parties submit to the non‑exclusive jurisdiction of
              the state and federal courts located in Fulton County, Georgia.
            </p>
            <h2>8. Assignment</h2>
            <p>
              8.1 You may not assign your rights or obligations under this
              Agreement without our prior written consent (not to be
              unreasonably withheld). We may assign in connection with a
              reorganization, merger, sale of assets, or similar transaction.
            </p>
            <h2>9. Variation of these Conditions</h2>
            <p>
              9.1 We may vary these Conditions by publishing an updated version
              on our website or client portal. You accept by doing this, We have
              provided You with sufficient notice of this variation. We are
              under no other obligation to notify You of any variation to these
              terms and conditions.
            </p>
            <h2>10. Quotes</h2>
            <p>
              10.1 Quotes are valid for seven (7) days unless otherwise
              specified. A quote is an invitation to order only; your acceptance
              does not create a binding contract until we confirm and (if
              required) receive payment.
            </p>
            <p>
              10.2 On expiry, a quote is withdrawn and must be re‑requested if
              you wish to proceed.
            </p>
            <p>
              10.3 After confirmation by both parties, the quote price becomes
              the final agreed price (subject to mutually agreed changes).
            </p>
            <p>
              10.4 Prices may change before confirmation due to product or price
              changes you request. We may alter products/prices until a quote is
              confirmed.
            </p>
            <p>
              10.5 Quotes/estimates reflect your specifications and costs at the
              time given; subsequent changes you request (if accepted) are
              charged at prevailing Rates.
            </p>
            <p>
              10.6 When a quote converts to an Order, the Order is subject to
              these Conditions.
            </p>
            <p>
              10.7 Typical quote turnaround is ~24 hours; tell us if urgent.
            </p>
            <p>
              10.8 Where special pricing/discounts apply, no other
              promotion/discount/bonus applies.
            </p>
            <p>
              10.9 If external price/supply changes occur, we may update the
              quote. Discontinued items may be substituted subject to your
              approval.
            </p>
            <p>
              10.10 Prices for non‑stock items are subject to change and are
              confirmed only upon conversion of a quote to an Order.
            </p>
            <p>10.11 We may cancel a quote after expiry without notice.</p>
            <p>
              10.12 ETA information is based on an estimate given by our vendors
              and cannot be held as the actual promised date.
            </p>
            <p>
              10.13 Freight charges are added unless otherwise stated; any
              included delivery charge is an estimate.
            </p>
            <p>
              10.14 We do not keep inventory and order items upon receipt of a
              completed Order. Returns/cancellations may incur restocking fees
              and are subject to distributor approval.
            </p>
            <p>10.15 Bundle pricing may assume total quote purchase.</p>
            <p>
              10.16 Unless specified, quoted hardware items carry the
              manufacturer's return‑to‑depot warranty for parts and labor.
            </p>
            <p>
              10.17 We may vary or withdraw a quote at any time prior to
              confirmation at our discretion (e.g., unavailability or supplier
              cost increases).
            </p>
            <h2>11. Orders</h2>
            <p>
              11.1 Order forms: You may place an Order for Goods and/or Services
              with Us. Normally, We will require that You provide either a
              completed Order form or You approve the quote electronically via
              either an email or a web based system with the date and Your
              details, including Your full legal name or description and any
              applicable tax ID (including the full name or description of any
              person on whose behalf the order is placed), Your address together
              with any relevant Quote number and date.
            </p>
            <p>
              11.2 Approval of Orders: You will need to sign the Order or have
              it duly executed on Your behalf, unless the Order is sent by email
              or via the web based ordering system, in which case the Order will
              be treated or deemed as if signed by or on behalf of You by the
              person whose name appears as the sender of the email or submitter
              of the form.
            </p>
            <p>
              11.3 Reliance on appearance of validity: Absent actual knowledge
              to the contrary, We may rely upon the apparent validity of an
              Order. If any Order is signed or sent by email or approved through
              the web-based ordering system by a named person, that person
              warrants that the Order is, and it is acknowledged the Order is
              deemed in favor of Us to be:
            </p>
            <p>
              11.3.1 signed by, and duly authorized by, both the person who
              signed the Order and the person who sent the email; and
            </p>
            <p>
              11.3.2 duly authorized by the person on whose behalf the Order is
              placed or apparently placed.
            </p>
            <p>
              11.4 Acceptance and Orders: An Order has no effect unless or until
              it is accepted by You in writing and, until We have received from
              You payment in clear funds for the Order and any related freight,
              delivery and (where applicable) in-transit insurance costs in
              clear funds.
            </p>
            <p>
              11.5 No obligation to deliver: We are not obliged to deliver any
              Order until we have received payment in clear funds from You for
              the Order, any related freight, delivery and (where applicable)
              in-transit insurance costs or where We are unwilling or unable to
              complete the Order for any reason provided it refunds any payment
              made by You in respect of the Order.
            </p>
            <p>
              11.6 Credit checks: For the purposes of ascertaining the credit
              standing or history of a prospective customer to whom We are
              considering extending credit or payment terms, You hereby consents
              to Us undertaking a credit reference check in respect to You.
            </p>
            <p>
              11.7 Cancellation of Orders: You will not cancel an Order unless
              We agree to do so in writing in Our absolute discretion. You
              acknowledge that, amongst other things, We cannot cancel an Order
              once the manufacturer or supplier has dispatched the relevant
              Goods and that such dispatch often occurs the same day as the
              Order is placed by Us.
            </p>
            <p>
              11.8 Processes and Procedures: We have processes and procedures
              that We follow in the course of the provision of Our Services and
              the supply of Goods. You agree to co-operate with Us and to comply
              with such processes and procedures as advised to You from time to
              time.
            </p>
            <h2>12. Pricing and Rates</h2>
            <p>
              12.1 Rates exclude Tax: All rates and amounts charged or quoted
              for Goods and/or Services by Us are exclusive of Tax and any other
              applicable taxes or government charges (unless otherwise stated in
              writing by Us).
            </p>
            <p>
              12.2 Rates Schedule: You must pay for Goods and Services at the
              Rates set out in any applicable Plan and the Rate Schedule as
              applicable from time to time during the provision of the Goods
              and/or Services.
            </p>
            <p>
              12.3 Vary Rates: We reserve the right vary any Rate and/or the
              Rate Schedule from time to time (subject to any fixed pricing for
              specific periods in any Plan), in its absolute discretion and
              without notice to You.
            </p>
            <p>
              12.4 Call-out fees: You acknowledge that call-out fees may be
              charged in addition to the Rates at Our absolute discretion and
              that the amount of the call-out fee will depend upon where the
              Services are provided.
            </p>
            <p>
              12.5 Return/Cancellation Fee: Where We arrange a return or refund
              on behalf of You, or where an Order is cancelled by You after
              acceptance by Us, We may charge You a Return/Cancellation fee to
              cover the administration costs to Us in processing the return or
              refund, or in processing the Order, the cancellation and any
              refund. We may deduct the Return/Cancellation fee from out of any
              moneys otherwise due to be refunded to You by Us.
            </p>
            <p>
              12.6 Expenses: You must pay any out-of-pocket expenses incurred by
              Us in providing the Services to You in addition to the Rates,
              charges and call-out fees, upon written demand. Such expenses will
              include travel costs, flights, car hire, petrol, insurance, taxi
              fares, accommodation and related meal allowance, tolls and car
              parking expenses. Where appropriate, We will obtain prior written
              authorization from You before such expenses are incurred.
            </p>
            <p>
              12.7 Separate charges for Goods and Services: We may in Our
              absolute discretion charge for Goods separately from Services or
              may charge for Goods and Services together.
            </p>
            <p>
              12.8 Calculation of increments: Where a charge is calculated based
              on increments of time, e.g. 1 hour or 30 minutes, We will charge
              the applicable rate for the whole increment of time even if work
              is done during part of, but not for the whole of, that increment
              of time.
            </p>
            <p>
              12.9 Change in underlying costs: Without prejudice to any other
              rights of Ours under these Conditions, where there is any increase
              in the underlying costs incurred by Us in connection with the
              supply of Goods or Services to You, We may, in our absolute
              discretion, vary any of Our Rates.
            </p>
            <p>
              12.10 Pre-Paid Blocks of Service: Where You agree to buy Pre Paid
              Blocks of Service during a Period, payment must be made in advance
              for the Pre-Paid Blocks of Service at the rate applicable pursuant
              to the Rates Schedule for all Services. Each such rate being less
              any discount agreed in writing between Us and You in respect of
              the Pre-Paid Blocks of Service. Services included in a Pre Paid
              Block of Service rate during the Period:
            </p>
            <p>
              12.10.1 are calculated in accordance with the applicable minimum
              time periods and increments set out in the Rates Schedule; and
            </p>
            <p>
              12.10.2 are only provided by Us during the applicable Period.
              Where Services are provided for a specified Period:
            </p>
            <p>
              12.10.2.1 the Services remaining unused for that Period cannot be
              rolled over into any subsequent Period; and
            </p>
            <p>
              12.10.2.2 We are not liable to refund, re-imburse, pay damages or
              otherwise compensate or indemnify You in respect of those unused
              Services.
            </p>
            <h2>13. Services and Plans</h2>
            <p>
              13.1 We may modify the scope, terms, or availability of Services
              and Plans from time to time. Current offerings are described in
              the Rate Schedule and any applicable Plan Schedule(s).
            </p>
            <p>
              13.2 We will provide the current Rate Schedule upon request. Plan
              Schedules are provided to clients enrolled in the applicable
              Plan(s).
            </p>
            <h2>14. Contracting</h2>
            <p>
              14.1 We may subcontract any or all Services but remain responsible
              for performance under these Conditions.
            </p>
            <h2>15. Delivery, Title, and Risk</h2>
            <p>
              15.1 Delivery liability: We will use all reasonable endeavors to
              dispatch Goods by the due date, but do not accept any liability
              for non-delivery or failure to deliver on time where this is
              caused by circumstances beyond the reasonable control of Ours,
              including, for example, due to failures in supply to Us or delays
              caused by third parties, such as delivery companies or
              manufacturers.
            </p>
            <p>
              15.2 Availability to accept delivery: You must be available to
              accept the Goods at Your nominated delivery address during
              Business Hours unless otherwise arranged.
            </p>
            <p>
              15.3 Passing of Risk: Delivery is deemed to take place when the
              Goods are delivered to Your nominated address, whereupon risks of
              loss, breakage and all damage and all other risks pass to You.
              Nothing in this clause
            </p>
            <p>15.3 will affect title to the Goods.</p>
            <p>
              15.4 Obligation to insure: You will ensure that Goods are
              adequately insured from the time of delivery under clause 15.3.
            </p>
            <p>
              15.5 Retention of Title: Until We receive full payment in cleared
              funds for any moneys due to Us by You on any account or for any
              reason:
            </p>
            <p>
              15.5.1 title to, and property in, Goods supplied to You remain
              vested in Us and does not pass to You;
            </p>
            <p>
              15.5.2 You must hold those Goods as fiduciary bailee and agent for
              Us and must not sell them;
            </p>
            <p>
              15.5.3 You must keep those Goods separate from other goods and
              maintain the Goods and their labelling and packaging intact;
            </p>
            <p>
              15.5.4 Where You sell the goods in breach of these Conditions, You
              are required to hold the proceeds of any sale of those Goods on
              trust for Us in a separate account (however any failure to do so
              will not affect Your obligation to deal with the proceeds as
              trustee and remit them to Us);
            </p>
            <p>
              15.5.5 We may, without prior notice, enter into any premises where
              We suspect those Goods may be, take possession of those Goods and
              sever and remove those Goods (notwithstanding that they may have
              been attached to other goods not the property of Ours) and for
              this purpose, You hereby irrevocably authorize and direct Us (and
              Our employees and agents) to enter into such premises as its duly
              authorized agent and You hereby indemnify and hold harmless Us
              from and against any costs, claims, allegations, demands, damages
              or expenses or any other acts or omissions arising from or in
              connection with, such entry, repossession or removal.
            </p>
            <p>
              15.5.6 You irrevocably appoint Us as Your attorney to do anything
              We consider necessary in order to enter such premises and
              repossess the Goods as contemplated by this clause 15.5.
            </p>
            <h2>16. Returns and Claims</h2>
            <p>
              16.1 General Returns Policy: Notwithstanding anything in these
              Conditions, You acknowledge that We supply Goods subject to all
              applicable conditions, including returns and claims policies, of
              any relevant manufacturer or supplier. You will accept Goods
              subject always to these Conditions and the terms of such
              conditions and will indemnify and hold Us harmless in respect of
              any further or other obligation or any failure or default on the
              part of that manufacturer or supplier.
            </p>
            <p>
              16.2 Customized Goods not returnable: Where Goods have some
              element of customization for You, are supplied pursuant to an
              Order for Goods that is in the opinion of Ours special or unusual,
              the Goods are obtained from overseas, the Goods are obtained from
              a supplier who is no longer trading, or the Goods are otherwise
              not readily returnable by Us to the manufacturer or supplier or
              any related services may not be cancelled, You may not return the
              Goods to Us or cancel the related services.
            </p>
            <p>
              16.3 Duty to inspect: You will inspect all Goods immediately upon
              their delivery. Within 7 days of such delivery You may give
              written notice to Us of any matter or thing, by reason of which
              You might wish to return the Goods, ask for a refund, or make a
              claim. If no such notice is given on time, You will accept the
              Goods without any such return, refund or claim.
            </p>
            <p>
              16.4 Return Condition: Where You are entitled to return Goods
              under these Conditions, You must return the Goods in their
              original condition and unopened, provided always that where, upon
              opening the packaging it becomes apparent that the Goods are
              different to what is described on the packaging or that the Goods
              are faulty, the Goods may be returned.
            </p>
            <p>
              16.5 Return costs: You will pay all costs and expenses incurred by
              Us in arranging the return of the Goods to a manufacturer or
              supplier and/or the cancellation of any related services unless
              that manufacturer or supplier pays such costs.
            </p>
            <p>
              16.6 Consequences of use, installation, customization or sale: You
              will indemnify and hold Us harmless in respect of all allegations
              and claims in respect of Goods once such Goods have been used,
              installed, customized or re-sold by You (without prejudice to the
              recourse of such a customer to the manufacturer of the Goods).
            </p>
            <h2>
              17. Computer Utility, Functionality, and Fitness for Purpose
            </h2>
            <p>
              17.1 Service limitations given the science of computing: You
              acknowledge that a reasonable incident of the Services may involve
              trial and error and that it is a science applied often in novel or
              unknown circumstances and involving experiment. In particular, You
              acknowledge that the Services may involve tests, troubleshooting,
              advice and recommendations that may prove incorrect or
              inappropriate, particularly in an attempt to cure a problem You
              are having. While We will make what We consider (in Our absolute
              discretion) to be all reasonable endeavors to provide appropriate
              tests, troubleshooting, sound advice and good recommendations in
              order to assist You, You will always indemnify and hold Us
              harmless in the provision of our Services to You.
            </p>
            <p>
              17.2 Reasonable Assistance Limits: We are only obliged to provide
              what We consider, in Our absolute discretion, to be reasonable
              assistance in the circumstances (including with the installation
              and customization of new software or hardware for You or any other
              Work) under any Plan and You will pay for additional work at the
              Rates unless otherwise agreed.
            </p>
            <p>
              Without limiting the discretion of Us to determine what reasonable
              assistance is, normally, reasonable assistance is limited to work
              done during Business Hours over a period of time not exceeding any
              period that We have allowed or allows for the Work or has
              estimated or estimates the Work will take, whether or not notice
              of the time allowed or estimated is given by Us to You.
            </p>
            <p>
              17.3.3 You acknowledge that We have no control over many factors
              involved with the suitability, function or fitness for purpose of
              Goods in an existing or new computer environment, e.g.
            </p>
            <p>
              17.3.3.1 the compatibility or ability of the Goods to fit into or
              perform to expectations in the receiving computer/internet
              environment; or
            </p>
            <p>
              17.3.3.2 the behavior of third-party supplier, e.g. in relation to
              support;
            </p>
            <p>
              17.3.4 You acknowledge that for a whole number of reasons outside
              of Our control, the Goods may fail to meet Your expectations, may
              not turn out to be fit for all or any of the purposes sought, may
              not be suitable or may not function properly in all or any
              respects;
            </p>
            <p>
              17.3.5 You acknowledge that the Services provided by Us may
              involve the very task of seeking to customize Goods so they may be
              fit for particular purposes and that customization may be a very
              substantial project in itself;
            </p>
            <p>
              17.3.6 Accordingly, You will accept the sole responsibility for,
              and indemnify and hold Us harmless in respect of:
            </p>
            <p>
              17.3.6.1 decisions as to whether or not to follow recommendations
              by Us;
            </p>
            <p>
              17.3.6.2 decisions as to whether or not to purchase or customize
              Goods or obtain Services for that or any other purpose; and
            </p>
            <p>
              17.3.6.3 any failure or defect in suitability, function or fitness
              for purpose of any Goods and/or Services, including a
              responsibility to obtain Your own independent advice or second
              opinion from a suitably qualified person;
            </p>
            <p>
              17.3.7 Where We provide Services with a view to achieving Your
              purposes, suitability, function or fitness for purpose (whether
              expressed, agreed or otherwise), You must pay for those Services
              on time without any set-off or counter-claim, whether or not We
              are able to achieve any of such purposes, suitability, function or
              fitness for purpose, provided always that We have acted in good
              faith and have made what We consider, in Our absolute discretion,
              to have made all reasonable endeavors to achieve those outcomes.
            </p>
            <p>
              17.4 Testing Procedures: You will follow the instructions of Ours
              with regard to testing or troubleshooting any problems and that if
              those do not resolve the outstanding problems, We will, subject to
              these Conditions, allocate such resources as We consider
              reasonable in the circumstances towards their resolution.
            </p>
            <h2>18. Force Majeure</h2>
            <p>
              18.1 Force Majeure: If We are unable to supply any Goods or
              Services due to circumstances beyond Our reasonable control, We
              may cancel the Order (even if the Order has already been accepted)
              or cease to provide the Services by written notice to You, in
              which case You will hold Us harmless.
            </p>
            <p>
              18.2 We will not be liable for any breach of contract due to any
              matter or thing beyond Our control, including failures by third
              parties to supply goods, services or transport, stoppages,
              transport breakdown, fire, flood, earthquake, acts of God,
              strikes, lockouts, work stoppages, wars, riots or civil commotion,
              intervention or public authority, explosion or accident.
            </p>
            <h2>19. Product Specifications</h2>
            <p>
              19.1 Alterations to Specifications: We make every effort to supply
              the Goods in accordance with the Order however We may supply
              alternate Goods subject to minor variations in actual dimensions
              and specifications where these are changed by the manufacturer of
              the Goods after the Order date and before delivery.
            </p>
            <p>
              19.2 Substitute Goods: If We cannot supply the Goods ordered by
              You, We may supply alternate Goods of equal or superior quality
              provided however that You will not pay a higher price than the
              price Quoted or otherwise agreed for the Goods ordered.
            </p>
            <h2>20. Warranties</h2>
            <p>
              20.1 Reliance on Manufacturer's Warranty: You will rely on the
              warranties provided by the manufacturer of Goods supplied by Us
              (where applicable) and will deal directly with such manufacturer
              rather than Us for all claims covered by such warranties.
            </p>
            <p>
              20.2 No claim for manufacturer's default: You indemnify and hold
              Us harmless in respect of the performance or otherwise, by any
              manufacturer of Goods supplied to You by Us, of any of the
              obligations of such manufacturer in respect of such Goods. This
              includes any damages or moneys due to You arising under, or in
              connection with, any breach by the manufacturer of any the
              manufacturer's warranties in respect of the Goods.
            </p>
            <h2>21. Liability</h2>
            <p>
              21.1 Exclusion: Except as specifically set out herein and so far
              as may be permitted by law, any term, condition or warranty in
              respect of the quality, fitness for purpose, condition,
              description, assembly, manufacture, design or performance of the
              Goods or Services, whether implied by statute, common law, trade
              usage, custom or otherwise, is hereby expressly excluded.
            </p>
            <p>
              21.2 No liability for program or data loss: You indemnify and hold
              Us harmless in respect of any allegation, claim, loss or expense
              of Yours or any third party for any program or data loss or damage
              suffered by You or that third party arising directly or indirectly
              from the supply of the Goods or Services by Us to You. You
              acknowledge You are solely responsible for backing up Your
              programs and data in order to mitigate Your own potential loss of
              programs and data.
            </p>
            <p>
              21.3 Limit on consequential damage: You indemnify and hold Us
              harmless in respect of any allegation or claim as to any indirect
              or consequential losses or expenses suffered by You or any third
              party, howsoever caused, including but not limited to loss of
              turnover, profits, business or goodwill or any liability to You or
              any third party.
            </p>
            <p>
              21.4 Limit on damage from a failure in supply: You indemnify and
              hold Us harmless for any allegation or claim for loss or damage by
              You or a third party where We have failed to meet any delivery
              date or cancels or suspends the supply of Goods or Services.
            </p>
            <p>
              21.5 General limit on liability: Except as otherwise expressly
              stated in these terms and conditions, We are not liable for any
              loss or damage of any kind however caused (including, but not
              limited to, by the negligence of Us) which is suffered or incurred
              by You in connection with:
            </p>
            <p>21.5.1 Goods or Services provided to You or any Work;</p>
            <p>21.5.2 these Terms and Conditions;</p>
            <p>
              21.5.3 Your use of Our website (including the use of a credit card
              or other debit device) or any linked website;
            </p>
            <p>
              21.5.4 the non-availability of Goods or Our Services for any
              reason;
            </p>
            <p>
              21.5.5 any act or omission of Ours or the provision of inaccurate,
              incomplete or incorrect information by You, or
            </p>
            <p>21.5.6 for any other reason whatsoever.</p>
            <p>
              21.6 Limitation options: To the extent that any legislation
              implies a condition or warranty that cannot be excluded but can be
              limited, clause 21.5 does not apply to that liability and Our
              liability for any breach of that condition or warranty is limited
              to Our doing any one or more of the following (at its election):
            </p>
            <p>
              21.6.1 replacing the Goods or supplying equivalent Goods, Services
              or Work;
            </p>
            <p>21.6.2 repairing the Goods or the Work;</p>
            <p>
              21.6.3 paying the cost of replacing the Goods or the Work or
              acquiring equivalent Goods, Services or Work; or
            </p>
            <p>
              21.6.4 paying the cost of having the Goods or the Work repaired.
            </p>
            <p>
              21.7 Laws still apply: Nothing in these Conditions is to be
              interpreted as excluding, restricting or modifying or having the
              effect of excluding, restricting or modifying the application of
              any State or Federal legislation applicable to the supply of the
              Goods or Services which cannot be excluded, restricted or
              modified.
            </p>
            <p>
              21.8 Severance: If any provision contained in the Conditions is
              unlawful, invalid or unenforceable, those provisions may be
              severed without prejudice to the validity and enforceability of
              the remaining provisions of the Conditions.
            </p>
            <h2>22. Errors and Omissions</h2>
            <p>
              22.1 We make every effort to ensure that all prices and
              descriptions quoted are correct and accurate. In the case of an
              error or omission, We may rescind the affected contract by written
              notice to You, notwithstanding that We have already accepted Your
              Order and/or received payment from You. Our liability in that
              event will be limited to the return of any money You have paid in
              respect of the Order.
            </p>
            <h2>Our Responsibilities</h2>
            <h2>23. Privacy Statements and Your Rights</h2>
            <p>
              23.1 We are collecting Your personal information for the
              fulfilment of Quotes, Orders and the provision of Goods or
              Services to you and it may retain and use it for any such purposes
              ("Authorized Purposes").
            </p>
            <p>
              23.2 You are required to provide your personal information to Us
              for Authorized Purposes.
            </p>
            <p>
              23.3 We may disclose Your personal information to other persons
              for the purposes of the fulfilment of Quotes, Orders and Work for
              you or in order to provide Goods or Services to You, to verify the
              information You provide, for enquiries about Goods or Services
              that may be suitable for your purposes, or to confirm Your
              requirements, to anyone proposing to supply Goods or Services to
              You, or to acquire Goods or Services on Your behalf, or in respect
              of enquiries relating to any of the foregoing.
            </p>
            <p>
              23.4 Otherwise We will not disclose Your personal information
              without Your consent unless authorized by law.
            </p>
            <p>
              23.5 Your personal information will be held by Us at Our Principal
              Place of Business and You can contact Us to request to access or
              correct it.
            </p>
            <p>
              23.6 We rely on You to submit correct information and details
              where requested. You accept that You may incur additional expenses
              if you submit incorrect information.
            </p>
            <h2>24. Our Website</h2>
            <p>
              24.1 We make no representations or warranties in relation to
              information available on Our website, including without
              limitation:
            </p>
            <p>
              24.1.1 that the information on Our website is complete or correct;
            </p>
            <p>
              24.1.2 that Our website will be continuously available or free
              from any delay in operation or transmission, virus, communications
              failure, internet access difficulties or malfunction in hardware
              or software; and that We endorse any internet site linked to Our
              website or any third-party products or services referred to on Our
              website.
            </p>
            <h2>25. Insurance Coverage</h2>
            <p>
              25.1 We will maintain at Our own expense, commercial general
              liability insurance for personal injury and property damage for a
              general aggregate of $1,000,000. At Your request We will provide
              You with certificates, including renewal certificates evidencing
              such coverage within thirty (30) days of commencing this
              Agreement, at every renewal and at other times as may be
              reasonably requested by You.
            </p>
            <h2>Your Responsibilities</h2>
            <h2>26. Lodging of Service Requests</h2>
            <p>
              26.1 In order for Us to provide You with the agreed Service, You
              agree to follow Our process for lodging of Service Requests as
              outlined in Appendix A.
            </p>
            <h2>27. Access to Systems, Sites and People</h2>
            <p>
              27.1 In order to provide You with the agreed Service, You agree to
              give Us access to various items of Yours including but not limited
              to, equipment, people and sites as and when required.
            </p>
            <p>
              27.2 You agree to allow Us to install software on Your Equipment
              that allows Our technicians to access Your systems at any time.
              This software allows Us to view system statuses, send monitoring
              information, see users' desktops and control Your PCs and other
              hardware. This may require that devices are left on overnight or
              weekends.
            </p>
            <h2>28. Third Party Authorizations</h2>
            <p>
              28.1 At times We may need to contact Your third-party providers on
              Your behalf, such as Your internet provider. Some of these
              providers may require Your authorization for Us to deal on Your
              behalf. It is Your responsibility to ensure that We are able to
              deal freely with these providers.
            </p>
            <h2>29. Payment, Late Payment and Default</h2>
            <p>
              29.1 Payment due date: All invoices issued to You are due and
              payable to Us within the terms stated on the invoice (unless
              otherwise agreed in writing). by cash, check, credit card or
              direct deposit in accordance with these Terms and Conditions and
              in the way set out in the Invoice.
            </p>
            <p>
              29.2 7 days late: Where You fail to pay an invoice within seven
              (7) days of the due date, We may, in Our absolute discretion and
              without prior notice, suspend or discontinue the supply of Goods
              and/or Services to You.
            </p>
            <p>
              29.3 Recoveries: All legal and other costs and expenses incurred
              in connection with the recovery of late payments will be added to
              the amount due by You to Us and will be recoverable from You, in
              addition to the original invoice cost. If You default in payment
              of any invoice on time, moneys which would have become due by You
              at a later date shall be immediately due and payable without any
              further notice to You. Collectively, all of these moneys are
              referred to in these Conditions as a "Sum Due".
            </p>
            <p>
              29.4 Interest: If payment of any Sum Due is not made on time, We
              will charge interest daily on the Sum Due at the maximum rate
              allowed by law, calculated and charged daily on and from the due
              date until the Sum Due is paid in full.
            </p>
            <p>
              29.5 Application of funds: All payments of the Sum Due made by You
              to Us will be applied as follows:
            </p>
            <p>
              29.5.1 first in or towards payment of any costs (including legal
              costs), charges, expenses or outgoings paid by Us in relation to
              any dishonored check fees, collection costs or any other action
              taken by Us for the recovery of any amounts owing by You to Us;
            </p>
            <p>
              29.5.2 secondly, in or towards payment of any interest due or
              payable hereunder, and
            </p>
            <p>
              29.5.3 thirdly, in or towards payment of Your debts to Us in order
              from the longest standing due to the most recently incurred.
            </p>
            <p>
              29.6 Security: We may require You to provide security over Your
              property (including the Goods or any other property of Yours) as
              collateral to be held as security for any Sum Due or as a
              condition precedent to the continuation of supply of Goods or
              Services by Us to You.
            </p>
            <p>
              29.7 Payment arrangements: In the event that a repayment
              arrangement is made in relation to any Sum Due and the supply of
              Goods or Services is resumed, but then a repayment due under that
              arrangement is not made on time, We may, in Our absolute
              discretion and without prior notice, again suspend or discontinue
              the supply of Goods or Services to You.
            </p>
            <p>
              29.8 Power of Attorney: You hereby irrevocably appoint Us as Your
              attorney to do anything We consider fit for the recovery of the
              Sum Due or the creation, perfection or enforcement of any
              collateral held or to be held as security for any Sum Due.
            </p>
            <p>
              29.9 Other remedies: We may exercise any of Our rights and
              remedies including taking legal action against You for the
              recovery of any moneys due to Us, notwithstanding it may have
              exercised other rights under these Conditions.
            </p>
            <h2>30. Non-Solicitation of Clients and Employees</h2>
            <p>
              30.1 You agree that employees are one of Our most valuable assets,
              policy and professional ethics require that Our employees not seek
              employment with, or be offered employment by You during the course
              of engagement and for a period of two (2) years thereafter (or the
              maximum amount permissible by a Court).
            </p>
            <p>
              30.2 You agree that Our damages resulting from breach of this
              clause 30.1 would be impracticable and that it would be extremely
              difficult for Us to ascertain the actual amount of damages.
              Therefore in the event You violate this provision, You agree to
              immediately pay Us 100% of the employee's total annual salary, as
              liquidated damages and We shall have the option to terminate this
              Agreement without further notice or liability to You. The amount
              of liquidated damages reflected herein is not intended as a
              penalty and is reasonably calculated based upon the projected
              costs We would incur to identify, recruit, hire and train suitable
              replacements for such personnel.
            </p>
            <h2>30. Software</h2>
            <p>
              31.1 All Software licenses are the responsibility of You and not
              that of Us. It is the duty of Yours to store all licenses for all
              Software used, so that that they can be reproduced if and when
              required. This includes all Software installed by Us.
            </p>
            <p>
              31.2 You indemnify and hold Us harmless against any claim,
              allegation, loss, damage or expense arising directly or indirectly
              from:
            </p>
            <p>31.2.1 any unauthorized Software use by You;</p>
            <p>
              31.2.2 any breach of any Software license in respect of Software
              provided to Us by You to be installed on one of Your computers;
            </p>
            <p>
              31.2.3 otherwise as a result of Us installing Software at Your
              where You are not authorized to use the Software; and
            </p>
            <p>
              31.2.4 any problem, defect or malfunction associated with any
              Software (or related services) supplied by third parties.
            </p>
            <p>
              31.3 All copyright in custom software remains the sole property of
              Ours unless alternate arrangements are made as part of a separate
              software agreement.
            </p>
            <h2>32. Copyright and Confidentiality</h2>
            <p>
              32.1 Warranty and breach: You warrant that any confidential or
              copyright information or intellectual property (of any kind and in
              any form held) or provided by You to Us belongs to You. In the
              event of any breach of this warranty, You will pay all sums due to
              Us as If such warranty had not been breached (and regardless of
              any non-performance of any obligation by Us on account of or in
              connection with the breach of such warranty). You indemnify and
              hold Us harmless in respect of any allegations, claims, loss,
              costs or expenses in connection with such breach of warranty by
              You.
            </p>
            <p>
              32.2 Retention of title: All copyright and other intellectual
              property rights in any Work created, commissioned or acquired by
              Us in the course of the supply of Services by Us to You will be
              the exclusive property of Ours unless otherwise agreed in writing
              by Us and You.
            </p>
            <p>
              32.3 Confidential Information: We acknowledge that in the course
              of providing Services to You, We may learn from You certain
              non-public personal and otherwise confidential information
              relating to You, including Your customers, consumers or employees.
              We shall regard any and all information We receive which in any
              way relates or pertains to You, including Your customers,
              consumers or employees as confidential.
            </p>
            <p>
              32.4 You also acknowledge that all information and services,
              consulting techniques, proposals, and documents disclosed by Us or
              which comes to Our attention during the course of business and
              provided under this agreement constitute valuable assets of, and
              confidential and/or proprietary information to Us. As such, both
              parties shall take all commercially reasonable steps to not
              disclose, reveal, copy, sell, transfer, assign, or distribute any
              part or parts of such information in any form, to any person or
              entity, or permit any of its employees, agents, or representatives
              to do so for any purpose except unless permitted in writing by the
              disclosing party or as required by applicable law.
            </p>
            <h2>Appendix A</h2>
            <h2>Service Request Lodgement Process</h2>
            <p>
              When you contact us to lodge a service request only the methods
              below must be used:
            </p>
            <p>Web Portal: https://portal.shift-it.io</p>
            <p>Email: support@shift-it.io</p>
            <p>
              Additional service request methods may be provided to customers.
              Include a short description of the problem and any screenshots of
              errors to assist in the resolution of the issue.
            </p>
            <p>
              If the issue is being lodged by either phone or external email you
              must include your name, company and return contact details.
            </p>
            <p>
              Service requests must not be lodged directly with technicians, as
              this detracts them from resolving the current issue.
            </p>
            <p>
              Service Requests that must be addressed outside of business hours
              must be lodged by phone (charges apply for afterhours work). If
              not, the Service Request will be viewed on Our next Business Day.
            </p>
          </Content>
        </Article>
      </Main>
      <Footer />
    </Page>
  );
}
